import { Fragment, useMemo } from 'react';

import { isEmpty } from 'lodash';
import { useTranslations } from 'next-intl';

import { useVerification } from '@/contexts/VerificationContext/useVerification';

import { normalizePrice } from '@/helpers/transformations.helpers';

import { PeriodEnum } from '@/enums/subscription-type.enums';

import { Button } from '@/components/Buttons';
import { Spinner } from '@/components/Spinner';

import { useStore } from '@/contexts/StoreContext';
import styles from '@/contexts/VerificationContext/VerificationModal/SubscriptionVerification/PaymentActionButton.module.scss';
import { useSubscriptionVerification } from '@/contexts/VerificationContext/VerificationModal/SubscriptionVerification/contexts/SubscriptionVerificationProvider';

// =================================================================

export const PaymentActionButton = () => {
  const t = useTranslations();

  const {
    writeOff,
    isOnlyBalancePurchasing,
    userCredentials,
    purchaseSubscription,
    isPurchasingPending,
    promoCodeResponse,
  } = useSubscriptionVerification();

  const { subscription: selectedSubscription, setSubscription } = useVerification();

  const { subscriptionPeriod } = useStore(store => store.subscriptionStore);

  const matchedPromo = useMemo(() => {
    if (!promoCodeResponse || !selectedSubscription) return null;
    if (selectedSubscription?.period[subscriptionPeriod].promo_code)
      return {
        discounted_price: selectedSubscription?.period[subscriptionPeriod].price,
        original_price: selectedSubscription?.period[subscriptionPeriod].old_price,
      };
    return promoCodeResponse.find(
      tariff =>
        tariff.tariff_id === selectedSubscription.id &&
        ((tariff.days === 30 && subscriptionPeriod === PeriodEnum.Month) ||
          (tariff.days !== 30 && subscriptionPeriod === PeriodEnum.Year)),
    );
  }, [promoCodeResponse, selectedSubscription, subscriptionPeriod]);

  return (
    <div className={styles.paymentActionWrapper}>
      <div className={styles.paymentAmounts}>
        {!isEmpty(matchedPromo) ? (
          <Fragment>
            <p className={styles.promocodeTitle}>{t('verification.withPromoCode')}</p>
            <div className={styles.price}>
              {t('verification.price', {
                price: normalizePrice(Number(matchedPromo?.discounted_price)) ?? 0,
              })}
              <div className={styles.price__deletedPrice}>
                {t('verification.price', {
                  price: normalizePrice(Number(matchedPromo?.original_price)) ?? 0,
                })}
              </div>
            </div>
          </Fragment>
        ) : (
          <div className={styles.price}>
            {t('verification.priceLabel', { price: normalizePrice(writeOff) ?? 0 })}
          </div>
        )}

        <p className={styles.description}>
          {isOnlyBalancePurchasing
            ? t('verification.balanceDeduction')
            : t('verification.cardDeduction')}
        </p>
      </div>
      <Button
        as="button"
        onClick={purchaseSubscription}
        variant={'primary'}
        size="lg"
        disabled={(!isOnlyBalancePurchasing && !userCredentials.card) || isPurchasingPending}
      >
        {t('verification.pay')}
        {isPurchasingPending && <Spinner className={styles.paymentSpinner} />}
      </Button>
    </div>
  );
};
